import { isAddress } from "viem";
import { z } from "zod";
import {
  DEFI_ASSETS,
  PORTFOLIO_ASSETS,
  STAKING_ASSETS,
  SWAP_ASSETS,
} from "../../../shared/utils/asset-config";
import { normalizeChainId } from "../../../shared/utils/formatters";

/**
 * Validates a simple chain identifier (non-CAIP-2)
 * Examples: "1", "42161", "cosmoshub-4"
 */
const validateChainId = (value: string): boolean => {
  return /^[a-zA-Z0-9_-]+$/.test(value);
};

/**
 * Validates Ethereum address checksum format
 * Uses EIP-55 checksum validation via viem
 */
const validateEthereumAddress = (address: string): boolean => {
  try {
    // viem isAddress function validates both format and EIP-55 checksum
    return isAddress(address);
  } catch {
    return false;
  }
};

/**
 * Zod schema for chain identifier (non-CAIP-2)
 */
const ChainIdSchema = z.string().refine(validateChainId, {
  message: "Must be a valid chain identifier (e.g., '1', '42161', 'cosmoshub-4')",
});

/**
 * Zod schema for Ethereum address with checksum validation
 */
const EthereumAddressSchema = z.string().refine(validateEthereumAddress, {
  message: "Must be a valid Ethereum address with proper EIP-55 checksum",
});

/**
 * Zod schema for protocol configuration
 */
export const LendBorrowMarkets = z.array(
  z.enum([
    "1200", // Aave V3 Ethereum
    "1202", // Aave V3 Testnet Sepolia
    "1302", // Compound V3 Testnet
    "1003", // Aave V2 Avalanche
    "1203", // Aave V3 Avalanche
    "1005", // Aave V2 Polygon
    "1205", // Aave V3 Polygon
    "1204", // Aave V3 Arbitrum
    "1206", // Aave V3 Base
    "1402", // Morpho Base
    "1207", // Aave V3 Optimism
  ]),
);

// Assets and chains use simple chain IDs (non-CAIP-2)

/**
 * Zod schema for widget features configuration
 */
/**
 * Base feature toggle schema: always an object with at least { enabled: boolean }.
 * Use loose() to allow forward-compatible extra fields without breaking validation.
 */
const BaseFeatureSchema = z
  .object({
    enabled: z.boolean(),
  })
  .loose();

/**
 * Asset configuration with chain ID and address
 */
const AssetConfigSchema = z.object({
  chainId: ChainIdSchema,
  address: EthereumAddressSchema,
});

/**
 * Shared schema for allow/block list filters
 */
const AllowBlockListSchema = z.object({
  allowList: z.array(z.string()).default([]),
  blockList: z.array(z.string()).default([]),
});

/**
 * Chains allow/block lists for simple chain IDs
 */
const ChainAllowBlockListSchema = z.object({
  allowList: z.array(ChainIdSchema).default([]),
  blockList: z.array(ChainIdSchema).default([]),
});

/**
 * Helper to get supported chain IDs from asset configs
 */
function getSupportedChainIds(assets: typeof DEFI_ASSETS): string[] {
  const chainIds = assets.flatMap((asset) => asset.networks.map((network) => String(network.id)));
  return Array.from(new Set(chainIds));
}

/**
 * Helper to create chain validation schema for a specific module
 * Validates that chain IDs in allowList are supported by the module's assets
 */
function createModuleChainSchema(moduleAssets: typeof DEFI_ASSETS, moduleName: string) {
  return ChainAllowBlockListSchema.default({ allowList: [], blockList: [] }).superRefine(
    (data, ctx) => {
      // If no restrictions, skip validation (empty allowList = all chains allowed)
      if (data.allowList.length === 0) {
        return;
      }

      // If module has no asset restrictions (empty array), any chain is allowed
      if (moduleAssets.length === 0) {
        return;
      }

      // Get supported chain IDs from module assets
      const supportedChainIds = getSupportedChainIds(moduleAssets);

      // Normalize both lists for comparison
      const normalizedSupported = supportedChainIds.map((id) => String(normalizeChainId(id)));
      const normalizedAllowList = data.allowList.map((id) => String(normalizeChainId(id)));

      // Check if all chains in allowList are supported
      const unsupportedChains = normalizedAllowList.filter(
        (chainId) => !normalizedSupported.includes(chainId),
      );

      if (unsupportedChains.length > 0) {
        ctx.addIssue({
          code: "custom",
          message: `${moduleName} module does not support chain ID(s): ${unsupportedChains.join(", ")}. Supported chains: ${normalizedSupported.join(", ")}`,
        });
      }
    },
  );
}

/**
 * Asset-specific schema for allow/block list filters with chainId and address
 */
const AssetAllowBlockListSchema = z.object({
  allowList: z.array(AssetConfigSchema).default([]),
  blockList: z.array(AssetConfigSchema).default([]),
});

/**
 * Swap-specific feature schema extending the base feature with allow/block lists.
 */
const SwapFeatureSchema = BaseFeatureSchema.extend({
  // Chains allow/block lists using simple chain IDs (validated against SWAP_ASSETS)
  chains: createModuleChainSchema(SWAP_ASSETS, "Swap").optional(),
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  dexes: AllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  bridges: AllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  fees: z
    .object({
      enabled: z.boolean(),
    })
    .default({ enabled: false }),
}).loose();

/**
 * DeFi-specific feature schema extending the base feature with allow/block lists
 * for chains and assets.
 */
const DefiFeatureSchema = BaseFeatureSchema.extend({
  // Optional protocol markets for DeFi
  markets: LendBorrowMarkets.optional(),
  // Chains allow/block lists using simple chain IDs (validated against DEFI_ASSETS)
  chains: createModuleChainSchema(DEFI_ASSETS, "DeFi").optional(),
  // Assets allow/block lists using simple chain IDs and checksummed addresses
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  customVaults: z.boolean().optional(),
})
  .loose()
  .superRefine((data, ctx) => {
    // Only validate if both chains and assets are present
    if (!data.chains || !data.assets) {
      return;
    }

    // Validate that asset chain IDs are consistent with chains allowList
    const chainsAllowList = data.chains.allowList;
    const assetsAllowList = data.assets.allowList;

    // If no chain restrictions, all asset chains are allowed
    if (chainsAllowList.length === 0) {
      return;
    }

    // If no asset restrictions, nothing to validate
    if (assetsAllowList.length === 0) {
      return;
    }

    // Normalize chain IDs for comparison
    const normalizedChains = chainsAllowList.map((id) => String(normalizeChainId(id)));

    // Find any asset chain IDs that aren't in the chains allowList
    const inconsistentAssets = assetsAllowList.filter((asset) => {
      const normalizedAssetChain = String(normalizeChainId(asset.chainId));
      return !normalizedChains.includes(normalizedAssetChain);
    });

    if (inconsistentAssets.length > 0) {
      const chainIds = inconsistentAssets.map((a) => a.chainId).join(", ");
      ctx.addIssue({
        code: "custom",
        path: ["assets"],
        message: `Asset chain IDs [${chainIds}] are not in the chains allowList. All asset chain IDs must be present in chains.allowList or chains.allowList must be empty.`,
      });
    }
  });

/**
 * Staking-specific feature schema extending the base feature with chain restrictions
 */
const StakingFeatureSchema = BaseFeatureSchema.extend({
  // Chains allow/block lists using simple chain IDs (validated against STAKING_ASSETS)
  chains: createModuleChainSchema(STAKING_ASSETS, "Staking").optional(),
}).loose();

/**
 * Portfolio-specific feature schema extending the base feature with chain restrictions
 */
const PortfolioFeatureSchema = BaseFeatureSchema.extend({
  // Chains allow/block lists using simple chain IDs (validated against PORTFOLIO_ASSETS)
  chains: createModuleChainSchema(PORTFOLIO_ASSETS, "Portfolio").optional(),
}).loose();

export const WidgetFeaturesSchema = z.object({
  portfolio: PortfolioFeatureSchema,
  staking: StakingFeatureSchema,
  swap: SwapFeatureSchema,
  defi: DefiFeatureSchema,
  walletInfo: z.boolean().default(true),
  walletMode: z.enum(["internal", "external"]),
});

/**
 * Logo configuration - supports either a single URL or separate light/dark logos
 */
const LogoConfigSchema = z.union([
  // Single logo URL for both themes
  z
    .string()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true;
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
  // Separate logos for light and dark themes
  z.object({
    lightLogo: z.string().refine(
      (val) => {
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
    darkLogo: z.string().refine(
      (val) => {
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
  }),
]);

/**
 * Width configuration - supports either device types or custom pixel values
 */
const WidthSchema = z.union([
  z.enum(["mobile", "tablet"]), // Device types
  z
    .number()
    .min(320)
    .max(1000), // Custom pixel width
]);

/**
 * Height configuration - supports either device types or custom pixel values
 */
const HeightSchema = z.union([
  z.enum(["mobile", "tablet"]), // Device types
  z
    .number()
    .min(480)
    .max(1200), // Custom pixel height
]);

/**
 * ISO 4217 currency code validation
 * Supported currencies: USD (US Dollar), KRW (Korean Won), JPY (Japanese Yen)
 */
const ISO4217CurrencyCodeSchema = z.enum(["USD", "KRW", "JPY"], {
  message: "Must be one of: USD, KRW, JPY",
});

/**
 * Locale validation schema
 * Supported locales: en-US (English), ko-KR (Korean), ja-JP (Japanese)
 */
const LocaleSchema = z.enum(["en-US", "ko-KR", "ja-JP"], {
  message: "Must be one of: en-US, ko-KR, ja-JP",
});

/**
 * Zod schema for widget appearance configuration
 *
 * Note: The theme setting can be overridden by customers when embedding the widget
 * by adding a 'colorScheme' URL parameter (e.g., ?colorScheme=dark)
 * This allows customers to set the color scheme without modifying the server configuration.
 * Similarly, currency and locale can be overridden via the 'currency' and 'locale' URL parameters.
 */
export const WidgetAppearanceSchema = z.object({
  logoUrl: LogoConfigSchema.optional(),
  width: WidthSchema,
  height: HeightSchema.default("mobile"),
  theme: z.enum(["light", "dark"]).default("light"),
  builtByBlockdaemon: z.boolean().default(true),
  locale: LocaleSchema,
  currency: ISO4217CurrencyCodeSchema,
});

/**
 * Zod schema for protocol configuration
 */
export const DefiProtocolSchema = z.array(
  z.enum([
    "1200", // Aave V3 Ethereum
    "1202", // Aave V3 Testnet Sepolia
    "1302", // Compound V3 Testnet
    "1003", // Aave V2 Avalanche
    "1203", // Aave V3 Avalanche
    "1005", // Aave V2 Polygon
    "1205", // Aave V3 Polygon
    "1204", // Aave V3 Arbitrum
    "1206", // Aave V3 Base
    "1402", // Morpho Base
    "1207", // Aave V3 Optimism
  ]),
);

/**
 * Main widget configuration schema
 */
export const WidgetConfigSchema = z.object({
  features: WidgetFeaturesSchema,
  appearance: WidgetAppearanceSchema,
});

/**
 * Refined schema with additional business logic validation
 */
export const ValidatedWidgetConfigSchema = WidgetConfigSchema.refine(
  (config) => {
    // Ensure at least one core page feature is enabled (exclude walletMode and walletInfo)
    const enabledCoreFeatures = [
      config.features.portfolio.enabled,
      config.features.staking.enabled,
      config.features.swap.enabled,
      config.features.defi.enabled,
    ].filter(Boolean);
    return enabledCoreFeatures.length > 0;
  },
  {
    message: "At least one feature (portfolio, staking, swap, or defi) must be enabled",
    path: ["features"],
  },
);

// Export types inferred from schemas
export type WidgetFeatures = z.infer<typeof WidgetFeaturesSchema>;
export type WidgetAppearance = z.infer<typeof WidgetAppearanceSchema>;
export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;
export type ValidatedWidgetConfig = z.infer<typeof ValidatedWidgetConfigSchema>;

/**
 * Normalized feature flags for UI consumption: always booleans for feature pages,
 * while keeping wallet fields as-is. This allows the rest of the app to treat
 * features as simple flags even if the config uses object forms.
 */
export type NormalizedWidgetFeatures = {
  portfolio: boolean;
  staking: boolean;
  swap: boolean;
  defi: boolean;
  walletInfo: boolean;
  walletMode: "internal" | "external";
};

export function normalizeFeatures(features: WidgetFeatures): NormalizedWidgetFeatures {
  const get = (value: { enabled: boolean }): boolean => Boolean(value?.enabled);

  return {
    portfolio: get(features.portfolio),
    staking: get(features.staking),
    swap: get(features.swap),
    defi: get(features.defi),
    walletInfo: Boolean(features.walletInfo),
    walletMode: features.walletMode,
  };
}

/**
 * Validates a customer config object against the schema
 */
export function validateCustomerConfig(config: unknown): ValidatedWidgetConfig {
  try {
    return ValidatedWidgetConfigSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.issues
        .map((err) => {
          const path = err.path.join(".");
          return `${path}: ${err.message}`;
        })
        .join(", ");

      throw new Error(`Invalid customer config: ${errorMessages}`);
    }
    throw error;
  }
}

/**
 * Safely validates a config and returns result with error information
 */
export function safeValidateCustomerConfig(config: unknown): {
  success: boolean;
  data?: ValidatedWidgetConfig;
  error?: string;
} {
  try {
    const validatedConfig = validateCustomerConfig(config);
    return { success: true, data: validatedConfig };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown validation error",
    };
  }
}
